import { useExpanded, useSortBy, useTable } from 'react-table';
import PropTypes from 'prop-types';
import TableMui from '@mui/material/Table';
import { styled } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Paper, Box } from '@mui/material';
import { ArrowDownward, ArrowUpward, UnfoldMore } from '@mui/icons-material';
import NoData from '../NoData';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.common.black,
}));

const styles = {
  tableBoldHeaders: {
    fontWeight: '700 !important',
    padding: '16px',
  },
  weeksHeaderWidth: {
    maxWidth: '50px',
  },
  sticky: {
    position: 'sticky',
    left: '0',
    background: 'white',
    // boxShadow: '5px 2px 5px grey',
  },
  stickyAudience: {
    left: '162px',
  },
  stickyYTDPercent: {
    left: '208px',
  },
  stickyTotal: {
    left: '344px',
  },
  stickyYTD: {
    position: 'sticky',
    left: '168px',
  },
  stickyVendor: {
    left: '151.7px',
  },
};

const useRowSpan = (instance) => {
  const { allColumns } = instance;
  let rowSpanHeaders = [];
  allColumns.forEach((column) => {
    const { id, enableRowSpan } = column;
    if (enableRowSpan) {
      rowSpanHeaders = [...rowSpanHeaders, { id, topCellValue: null, topCellIndex: 0 }];
    }
    Object.assign(instance, { rowSpanHeaders });
  });
};

const defaultPropGetter = () => ({});

const Table = ({
  data,
  columns,
  boldHeaders,
  boldDataColumns,
  stickyColumns,
  getCellProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  audienceAnalysisStyle = false,
  applyStyleToLastRow = false,
  applySort = false, // New prop to control sorting UI
  excludedRowNameForSort = '',
  spendsToggle = false,
  includeForSorting = [], // New prop to include columns for sorting
  initialSortBy = [], // New prop for initial sorting
  allowBorderForSticky = true,
  vendorFirstColumn = false,
}) => {
  const excludedRowName = excludedRowNameForSort; // to avoid the sorting of the particular row
  const customSortBy = (rowA, rowB, columnId, desc) => {
    if (rowA?.original?.Channel_Name === excludedRowName) return 1;
    if (rowB?.original?.Channel_Name === excludedRowName) return -1;

    // Default sorting logic
    if (rowA?.values?.[columnId] > rowB?.values?.[columnId]) {
      return desc ? -1 : 1;
    }
    if (rowA?.values?.[columnId] < rowB?.values?.[columnId]) {
      return desc ? 1 : -1;
    }
    return 0;
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, rowSpanHeaders } =
    useTable(
      {
        columns,
        data,
        initialState: { sortBy: initialSortBy?.map((id) => ({ id, desc: true })) },
      },
      (hooks) => hooks.useInstance.push(useRowSpan),
      applySort ? useSortBy : () => {},
      useExpanded
    );
  // Override the default sorting function
  if (applySort) {
    rows?.sort((a, b) => customSortBy(a, b, excludedRowNameForSort, false));
  }

  const renderCustomStyles = (_column) => {
    if (boldHeaders) {
      return styles.tableBoldHeaders;
    } else {
      return {};
    }
  };

  const renderStickyColumns = (column) => {
    if (stickyColumns?.includes(column.id)) {
      if (['audience', 'Vendor']?.includes(column.id)) {
        return column.id === 'Vendor'
          ? vendorFirstColumn
            ? styles.sticky
            : { ...styles.sticky, ...styles.stickyVendor }
          : { ...styles.sticky, ...styles.stickyAudience };
      } else if (column.id === 'total') {
        return spendsToggle
          ? { ...styles.sticky, ...styles.stickyAudience }
          : { ...styles.sticky, ...styles.stickyTotal };
      } else if (column.id === 'ytd_spend_to_plan' || column.id === '_YTD') {
        return { ...styles.sticky, ...styles.stickyYTD };
      } else if (column.id === 'ytd_perc_to_plan') {
        return {
          ...styles.sticky,
          ...styles.stickyYTDPercent,
          ...(!allowBorderForSticky && { border: 0 }),
        };
      }
      return allowBorderForSticky ? { ...styles.sticky } : { ...styles.sticky, border: 0 };
    }
  };

  // Function to determine the sorting icon
  const getSortingIcon = (column) => {
    if (column?.isSorted) {
      return column?.isSortedDesc ? (
        <ArrowDownward fontSize="small" />
      ) : (
        <ArrowUpward fontSize="small" />
      );
    } else {
      return <UnfoldMore fontSize="small" />;
    }
  };

  // Helper function to get header props based on sorting configuration
  const getHeaderPropsForSorting = (column) => {
    if (!applySort) {
      return undefined;
    }

    const shouldApplySorting =
      (includeForSorting?.length && includeForSorting.includes(column.id)) ||
      !includeForSorting?.length;

    return shouldApplySorting ? column.getSortByToggleProps() : undefined;
  };

  // Helper function to get cell styles based on bold and sticky configurations
  const getCellStyles = (cell) => {
    const isBold = boldDataColumns?.includes(cell.column.id);
    const isSticky = stickyColumns?.includes(cell.column.id);

    if (isBold && isSticky) {
      return {
        ...renderStickyColumns(cell.column),
        ...styles.tableBoldHeaders,
      };
    }

    if (isBold) {
      return styles.tableBoldHeaders;
    }

    return renderStickyColumns(cell.column);
  };

  return data?.length === 0 ? (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <NoData />
    </Box>
  ) : (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...(!audienceAnalysisStyle && { margin: '30px auto' }),
      }}
    >
      <TableContainer>
        <TableMui size="small" {...getTableProps()}>
          <TableHead>
            {headerGroups?.map((headerGroup) => (
              <StyledTableRow {...headerGroup?.getHeaderGroupProps()}>
                {headerGroup?.headers?.map((column) => (
                  <StyledTableCell
                    {...column.getHeaderProps(getHeaderPropsForSorting(column))}
                    sx={{
                      ...renderCustomStyles(column),
                      ...renderStickyColumns(column),
                      whiteSpace: 'pre',
                    }}
                  >
                    <span>
                      {applySort && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {column.render('Header')}
                          {includeForSorting?.length && includeForSorting.includes(column.id)
                            ? getSortingIcon(column)
                            : null}
                          {!includeForSorting?.length && getSortingIcon(column)}
                        </span>
                      )}
                      {!applySort && column.render('Header')}
                    </span>
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows?.map((row, i) => {
              prepareRow(row);
              for (let j = 0; j < row.allCells.length; j++) {
                let cell = row.allCells[j];
                let rowSpanHeader = rowSpanHeaders.find((x) => x.id === cell.column.id);
                if (rowSpanHeader) {
                  if (
                    rowSpanHeader.topCellValue === null ||
                    rowSpanHeader.topCellValue !== cell.value
                  ) {
                    cell.isRowSpanned = false;
                    rowSpanHeader.topCellValue = cell.value;
                    rowSpanHeader.topCellIndex = i;
                    cell.rowSpan = 1;
                  } else {
                    rows[rowSpanHeader.topCellIndex].allCells[j].rowSpan++;
                    cell.isRowSpanned = true;
                  }
                }
              }
              return null;
            })}
            {rows?.map((row) => {
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    if (cell.isRowSpanned) return null;
                    else {
                      return (
                        <TableCell
                          rowSpan={cell.rowSpan}
                          sx={getCellStyles(cell)}
                          {...cell.getCellProps([
                            {
                              className: cell.column.className,
                              style: cell.column.style,
                            },
                            getColumnProps(cell.column),
                            getCellProps(cell),
                          ])}
                        >
                          {cell.row.id === rows[rows.length - 1].id &&
                          !audienceAnalysisStyle &&
                          !applyStyleToLastRow ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: cell.value,
                              }}
                            />
                          ) : (
                            cell.render('Cell', {
                              isExpanded: true,
                            })
                          )}
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </TableMui>
      </TableContainer>
    </Paper>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  boldHeaders: PropTypes.bool,
  boldDataColumns: PropTypes.array,
  stickyColumns: PropTypes.array,
  getCellProps: PropTypes.func,
  getColumnProps: PropTypes.func,
  audienceAnalysisStyle: PropTypes.bool,
  applyStyleToLastRow: PropTypes.bool,
  applySort: PropTypes.bool,
  excludedRowNameForSort: PropTypes.string,
  spendsToggle: PropTypes.bool,
  includeForSorting: PropTypes.arrayOf(PropTypes.string),
  initialSortBy: PropTypes.arrayOf(PropTypes.string),
  allowBorderForSticky: PropTypes.bool,
  vendorFirstColumn: PropTypes.bool,
};

export default Table;
